import { Booking, FetchBookingsResponse } from "@/types/booking"
import { FolioDocumentAddEventResponse } from "@/types/folio/folio-document"
import { ItineraryEventUpdates } from "@/types/itinerary"
import { TripRef } from "@/types/trip"

import { baseApi } from "./base-api"
import { EventMutationResponse, itineraryApi } from "./itinerary-api"

export type { Booking }

export interface GetBookingsArgs {
  filter: string
  search: string
  /** Omitted, upcoming and past bookings are listed together. */
  timing?: "upcoming" | "past"
  hideCancelled?: boolean
  perPage?: number
}

export const bookingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBookings: builder.infiniteQuery<
      FetchBookingsResponse,
      GetBookingsArgs,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.meta.next_page ?? undefined,
      },
      query: ({
        queryArg: { filter, search, timing, hideCancelled, perPage },
        pageParam,
      }) => {
        const params = new URLSearchParams({
          filter,
          search,
          page: String(pageParam),
        })
        if (timing) params.append("timing", timing)
        if (hideCancelled) params.append("hide_cancelled", "true")
        if (perPage) params.append("per_page", String(perPage))
        return { url: `/api/bookings?${params}` }
      },
      providesTags: ["Bookings"],
    }),

    assignEvents: builder.mutation<
      FolioDocumentAddEventResponse,
      {
        eventIds: number[]
        tripRef: TripRef
        confirmed?: string
      }
    >({
      query: ({ eventIds, tripRef, confirmed }) => ({
        url: "/api/bookings/assign_events",
        method: "POST",
        data: { ids: eventIds, trip_ref: tripRef, confirmed },
      }),
      invalidatesTags: ["Bookings"],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // Moving a booking can affect both the destination trip and the
          // source trip it was removed from, so invalidate all itinerary caches.
          dispatch(itineraryApi.util.invalidateTags(["Itinerary"]))
        } catch {
          // If the mutation fails, do nothing
        }
      },
    }),

    unassignEvents: builder.mutation<FolioDocumentAddEventResponse, number[]>({
      query: (ids) => ({
        url: "/api/bookings/unassign_events",
        method: "POST",
        data: { ids },
      }),
      invalidatesTags: ["Bookings"],
      async onQueryStarted(_ids, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(itineraryApi.util.invalidateTags(["Itinerary"]))
        } catch {
          // If the mutation fails, do nothing
        }
      },
    }),

    // An uploaded receipt's event that is on no trip; "0" is the backend's itinerary id for those.
    updateReceiptEvent: builder.mutation<
      EventMutationResponse,
      { eventId: number; updates: ItineraryEventUpdates }
    >({
      query: ({ eventId, updates }) => ({
        url: `/api/itineraries/0/event/${eventId}`,
        method: "PUT",
        data: { event: updates },
      }),
      invalidatesTags: ["Bookings"],
    }),

    deleteEvents: builder.mutation<FolioDocumentAddEventResponse, number[]>({
      query: (ids) => ({
        url: "/api/bookings/delete_events",
        method: "POST",
        data: { ids },
      }),
      invalidatesTags: ["Bookings"],
      async onQueryStarted(_ids, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(itineraryApi.util.invalidateTags(["Itinerary"]))
        } catch {
          // If the mutation fails, do nothing
        }
      },
    }),
  }),
})

export const {
  useGetBookingsInfiniteQuery,
  useAssignEventsMutation,
  useUnassignEventsMutation,
  useUpdateReceiptEventMutation,
  useDeleteEventsMutation,
} = bookingsApi
